export const DB_TYPE_OPTIONS = ["PostgreSQL", "MySQL"];

export const EMPTY_DB_FORM = {
  dbType: "",
  host: "",
  port: "",
  database: "",
  username: "",
  testTable: "",
};

export const EMPTY_DB_CREDENTIALS = {
  password: "",
};

function filled(value) {
  return Boolean(String(value ?? "").trim());
}

/** True when the user (or prefill) has entered anything in the database surface. */
export function isDbSurfaceInUse(dbForm = {}, credentials = {}) {
  return (
    filled(dbForm.dbType) ||
    filled(dbForm.host) ||
    filled(dbForm.port) ||
    filled(dbForm.database) ||
    filled(dbForm.username) ||
    filled(dbForm.testTable) ||
    filled(credentials.password)
  );
}

const DEFAULT_PORTS = { postgresql: "5432", mysql: "3306" };

export function normalizeDbType(dbType) {
  const t = String(dbType || "").trim().toLowerCase();
  if (t.includes("mysql")) return "mysql";
  if (t.includes("postgres")) return "postgresql";
  return "";
}

export function defaultPortForDbType(dbType) {
  const norm = normalizeDbType(dbType);
  return DEFAULT_PORTS[norm] || "";
}

export function dbFormFromGate0Data(gate0 = {}) {
  const rawType = String(gate0.db_type || "").trim();
  const matched = DB_TYPE_OPTIONS.find(
    (opt) => opt.toLowerCase() === rawType.toLowerCase() || normalizeDbType(rawType) === normalizeDbType(opt)
  );
  return {
    dbType: matched || "",
    host: String(gate0.db_host || "").trim(),
    port: String(gate0.db_port || "").trim(),
    database: String(gate0.db_name || gate0.db_database || "").trim(),
    username: String(gate0.db_username || "").trim(),
    testTable: String(gate0.db_test_table || "").trim(),
  };
}

export function mergeDbFormIntoGate0Data(gate0 = {}, dbForm) {
  const norm = normalizeDbType(dbForm.dbType);
  return {
    ...gate0,
    db_type: String(dbForm.dbType || "").trim() || undefined,
    db_host: String(dbForm.host || "").trim() || undefined,
    db_port: String(dbForm.port || "").trim() || undefined,
    db_name: String(dbForm.database || "").trim() || undefined,
    db_username: String(dbForm.username || "").trim() || undefined,
    db_test_table: String(dbForm.testTable || "").trim() || undefined,
    db_engine: norm || undefined,
  };
}

export function dbFormCanSave(form = {}) {
  const isBlank = (v) => !String(v ?? "").trim();
  if (isBlank(form.dbType) || isBlank(form.host) || isBlank(form.database) || isBlank(form.username)) {
    return false;
  }
  const portNum = Number(String(form.port || "").trim());
  return Number.isInteger(portNum) && portNum >= 1 && portNum <= 65535;
}

export function buildDbSaveSummary(form, previous = null) {
  const dbType = String(form.dbType || "").trim();
  const keepReady =
    previous &&
    String(previous.db_type || previous.dbType || "").trim() === dbType &&
    String(previous.overall_status || "").toLowerCase() === "ready";
  return {
    ...(previous && typeof previous === "object" ? previous : {}),
    overall_status: keepReady ? "ready" : "saved",
    generated_at: keepReady ? previous.generated_at : new Date().toISOString(),
    db_type: dbType || null,
    host: String(form.host || "").trim() || null,
    port: String(form.port || "").trim() || null,
    database: String(form.database || "").trim() || null,
    username: String(form.username || "").trim() || null,
    table_check: keepReady ? previous.table_check || null : previous?.table_check || null,
  };
}

/** Drop the saved database surface so readiness no longer treats it as declared. */
export function clearDbFromGate0Data(gate0 = {}) {
  const next = { ...gate0 };
  delete next.db_type;
  delete next.db_host;
  delete next.db_port;
  delete next.db_name;
  delete next.db_database;
  delete next.db_username;
  delete next.db_test_table;
  delete next.db_engine;
  delete next.gate1_data_readiness_summary;
  return next;
}

function sameConfigValue(left, right) {
  return String(left ?? "").trim() === String(right ?? "").trim();
}

/**
 * Non-secret identity of the database form.
 * Password edits do not change this fingerprint.
 */
export function dbConfigFingerprint(form = {}) {
  return [
    normalizeDbType(form.dbType),
    String(form.host ?? "").trim(),
    String(form.port ?? "").trim(),
    String(form.database ?? "").trim(),
    String(form.username ?? "").trim(),
    String(form.testTable ?? "").trim(),
  ].join("\u0001");
}

export function tagDbReportWithForm(report, form) {
  if (!report || typeof report !== "object") return report;
  return {
    ...report,
    config_fingerprint: dbConfigFingerprint(form),
  };
}

/**
 * True when a stored database probe still belongs to the form on screen.
 * An empty database type matches, so hydration does not mark a saved result stale.
 */
export function dbReportMatchesForm(report, form) {
  if (!report || !form) return false;
  const reportType = normalizeDbType(report.db_type || report.dbType);
  const formType = normalizeDbType(form.dbType);
  if (!reportType) return false;
  if (!formType) return true;
  if (reportType !== formType) return false;
  if (report.config_fingerprint) {
    if (report.config_fingerprint === dbConfigFingerprint(form)) return true;
    // Username is cleared while the page is loading. Don't treat that alone as a new database.
    if (!String(form.username ?? "").trim()) {
      return (
        dbConfigFingerprint({ ...form, username: report.username || "" }) ===
        report.config_fingerprint
      );
    }
    return false;
  }
  const pairs = [
    ["host", report.host],
    ["port", report.port],
    ["database", report.database || report.db_name],
    ["username", report.username],
  ];
  for (const [key, reportVal] of pairs) {
    const formVal = String(form[key] ?? "").trim();
    if (!formVal || reportVal == null || reportVal === "") continue;
    if (!sameConfigValue(formVal, reportVal)) return false;
  }
  const formTable = String(form.testTable ?? "").trim();
  const reportTable = report.table_check?.table || report.test_table || "";
  if (formTable && reportTable && !sameConfigValue(formTable, reportTable)) return false;
  return true;
}

/** Probe to show as the current database result. A mismatch is not current. */
export function currentDbReport(report, form) {
  if (!report || !dbReportMatchesForm(report, form)) return null;
  return report;
}

/**
 * Argument for declared data checks.
 * A mismatched saved probe must not fall through as the current database result.
 * Returns null only when the extractor may use a still-matching saved summary.
 */
export function dbReportForDeclaredChecks(report, savedSummary, form) {
  if (dbReportMatchesForm(report, form)) return report;
  if (!report && (!savedSummary || dbReportMatchesForm(savedSummary, form))) return null;
  return { overall_status: "stale" };
}

export function buildDbConfigPayload(dbForm, credentials = {}) {
  const host = String(dbForm.host || "").trim();
  const database = String(dbForm.database || "").trim();
  const username = String(dbForm.username || "").trim();
  const password = String(credentials.password ?? "");

  if (!host) return { error: "Database host is required." };
  if (!database) return { error: "Database name is required." };
  if (!username) return { error: "Database username is required." };
  if (!password) return { error: "Database password is required for the live test (not stored)." };

  const norm = normalizeDbType(dbForm.dbType) || "postgresql";
  const port = parseInt(String(dbForm.port || "").trim(), 10);
  return {
    db_config: {
      db_type: norm,
      host,
      port: Number.isFinite(port) ? port : norm === "mysql" ? 3306 : 5432,
      database,
      username,
      password,
      test_table: String(dbForm.testTable || "").trim() || undefined,
    },
  };
}
